/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../logger';
import { Engine } from '../rules/engine';
import { GlobalFactStore } from '../rules/facts/store';
import { clone } from '../util';
import { ViewManager } from './activity';

import type { Facts } from '../rules/index';
import type { AdData, KeyValue } from '../types';
import type { HierarchyNode } from './activity';

const logger = Logger.getInstance();

class Ad {
  private data: AdData;
  private facts: Facts;
  private _keyValues: KeyValue[] = [];

  constructor(adData: AdData, facts: Facts) {
    this.data = clone(adData);
    this.facts = facts;
    this.setViewsFact();
  }

  public get deliveryId(): string {
    return this.data.id;
  }

  public set keyValues(keyValues: KeyValue[]) {
    this._keyValues = clone(keyValues);
  }

  public get linkedLineId(): string | null {
    return this.data.geid ?? null;
  }

  public get keyValues(): KeyValue[] {
    return clone(this._keyValues);
  }

  public isEligible(): boolean {
    if (!this.data?.rules?.conditions) {
      logger.warn(`Ad #${this.deliveryId} is missing conditions.`);
      return false;
    }
    const engine = new Engine(this.data?.rules?.conditions ?? {});
    const conditionsSatisfied = engine.run({
      ...GlobalFactStore.getInstance().getFacts(),
      ...this.facts,
    });
    return conditionsSatisfied;
  }

  public addFacts(fact: Facts): void {
    this.facts = { ...this.facts, ...fact };
  }

  private setViewsFact(): void {
    const hierarchy: HierarchyNode = {
      id: this.deliveryId,
      children: [],
    };
    this.facts['ad.views'] = ViewManager.getViewsForEntity(this.deliveryId, hierarchy);
  }
}

export { Ad };
